import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { defineStore, storeToRefs } from 'pinia';
import type { Step } from '@/widgets/Wizard/models/step';
import type { Config } from '@/widgets/Wizard/models/config';
import { useMetadataStore } from '@/widgets/Wizard/stores/metadata';
import { useReservationStore } from '@/widgets/Wizard/stores/reservation';
import { useRestartWizardStore } from '@/widgets/Wizard/stores/restart-wizard';
import { useCheckInStore } from '@/widgets/Wizard/stores/check-in';
import { usePaymentStore } from '@/widgets/Wizard/stores/payment';

export const useStepsStore = defineStore('booking-wizard/steps', () => {
    const i18n = useI18n();

    const metaDataStore = useMetadataStore();
    const { activeAccount } = storeToRefs(metaDataStore);

    const reservationStore = useReservationStore();
    const restartWizardStore = useRestartWizardStore();
    const checkInStore = useCheckInStore();
    const paymentStore = usePaymentStore();

    // State
    const steps = ref<Step[]>([]);
    const flows = ref<Config['flows'] | null>(null);

    const activeStepIndex = ref(0);
    const activeFlowKey = ref<string | null>(null);

    const showLoader = ref(false);

    // Getters
    const activeStep = computed(() => {
        if (steps.value.length === 0) {
            return null;
        }

        return steps.value[activeStepIndex.value] ?? null;
    });

    const activeFlow = computed(() => {
        if (flows.value && activeFlowKey.value && flows.value[activeFlowKey.value]) {
            return flows.value[activeFlowKey.value];
        }

        return null;
    });

    const nextStep = computed(() => {
        if (activeStepIndex.value + 1 >= steps.value.length) {
            return null;
        }

        return steps.value[activeStepIndex.value + 1];
    });

    const defaultFlowIsActive = computed(() => {
        return activeFlowKey.value === 'default';
    });

    const atFirstStepInFlow = computed(() => {
        if (activeStepIndex.value === 0 || steps.value.length === 0) {
            return true;
        }

        // Support being indirectly at the last step
        const firstStep = steps.value[0];
        if (activeStepIndex.value === 1 && firstStep && firstStep.shouldBeSkipped()) {
            return true;
        }

        return false;
    });

    const prevStep = computed(() => {
        if (activeStepIndex.value - 1 < 0) {
            if (defaultFlowIsActive.value) {
                return null;
            }

            if (!flows.value || !activeFlow.value) {
                return null;
            }

            if (activeFlow.value.childOf) {
                const parentFlow = flows.value[activeFlow.value.childOf];
                if (!parentFlow || parentFlow.steps.length === 0) {
                    return null;
                }

                return parentFlow.steps[parentFlow.steps.length - 1] ?? null;
            }

            const defaultFlow = flows.value.default;
            if (!defaultFlow || defaultFlow.steps.length === 0) {
                return null;
            }

            return defaultFlow.steps[defaultFlow.steps.length - 1] ?? null;
        }

        return steps.value[activeStepIndex.value - 1] ?? null;
    });

    const totalStepsAmount = computed(() => {
        if (!flows.value || !activeFlow.value) {
            return 0;
        }

        if (activeFlowKey.value === 'default') {
            let totalStepsCount = 0;

            for (const flowKey of Object.keys(flows.value)) {
                const flow = flows.value[flowKey];
                if (!flow) {
                    continue;
                }
                totalStepsCount += flow.steps.length;
            }

            return totalStepsCount;
        }

        const defaultStepsCount = flows.value?.default ? flows.value.default.steps.length : 0;
        const activeFlowStepsCount = activeFlowKey.value !== 'default' ? activeFlow.value.steps.length : 0;

        return defaultStepsCount + activeFlowStepsCount;
    });

    const completedStepsAmount = computed(() => {
        if (!flows.value) {
            return 0;
        }

        if (activeFlowKey.value === 'default') {
            return activeStepIndex.value + 1;
        }

        const defaultStepsCount = flows.value.default?.steps.length ?? 0;

        return defaultStepsCount + activeStepIndex.value + 1;
    });

    const canGoToNextStep = computed(() => {
        if (!nextStep.value || !activeStep.value) {
            return false;
        }

        if (!activeStep.value.canBeDeactivated(false, true)) {
            return false;
        }

        if (!nextStep.value.canBeActivated()) {
            return false;
        }

        return true;
    });

    const showProgressBar = computed(() => {
        if (!activeFlow.value) {
            return false;
        }

        return activeFlow.value.hasProgressBar;
    });

    // Actions
    const setSteps = (newSteps: Step[]) => {
        steps.value = newSteps;
    };

    const goToNextStep = () => {
        if (showLoader.value) {
            return false;
        }

        if (activeStep.value && !activeStep.value.canBeDeactivated(false, true)) {
            return false;
        }

        let stepIndexToAdd = 1;
        let possibleStep = nextStep.value;

        if (!possibleStep) {
            return false;
        }

        while (possibleStep.shouldBeSkipped()) {
            if (activeStepIndex.value + stepIndexToAdd + 1 >= steps.value.length) {
                return false;
            }

            stepIndexToAdd += 1;
            possibleStep = steps.value[activeStepIndex.value + stepIndexToAdd];
            if (!possibleStep) {
                return false;
            }
        }

        if (!possibleStep.canBeActivated()) {
            return false;
        }

        activeStep.value?.deactivate();
        activeStepIndex.value += stepIndexToAdd;
        activeStep.value?.activate();

        return true;
    };

    const goToPrevStep = () => {
        if (showLoader.value) {
            return false;
        }

        if (!activeFlow.value || !flows.value) {
            return false;
        }

        if (activeStep.value && !activeStep.value.canBeDeactivated(true, false)) {
            return false;
        }

        if (atFirstStepInFlow.value && !defaultFlowIsActive.value) {
            const prevFlowKey = activeFlow.value.childOf ?? 'default';

            if (!flows.value[prevFlowKey]) {
                return false;
            }

            return selectFlow(prevFlowKey, true);
        }

        let possibleStep = prevStep.value;
        let stepIndexToRemove = 1;

        if (!possibleStep) {
            return false;
        }

        while (possibleStep.shouldBeSkipped()) {
            if (activeStepIndex.value - (stepIndexToRemove + 1) < 0) {
                return false;
            }

            stepIndexToRemove += 1;
            possibleStep = steps.value[activeStepIndex.value - stepIndexToRemove] ?? null;
            if (!possibleStep) {
                return false;
            }
        }

        if (!possibleStep.canBeActivated()) {
            return false;
        }

        activeStep.value?.deactivate();
        activeStepIndex.value -= stepIndexToRemove;
        activeStep.value?.activate();

        return true;
    };

    const setFlows = (newFlows: Config['flows']) => {
        flows.value = newFlows;
    };

    const selectFlow = (newFlowKey: string, startAtEnd: boolean = false) => {
        if (!flows.value) {
            return false;
        }

        if (!flows.value[newFlowKey]) {
            return false;
        }

        if (activeStep.value && !activeStep.value.canBeDeactivated(false, false)) {
            return false;
        }

        const newFlow = flows.value[newFlowKey];
        if (!newFlow || newFlow.steps.length === 0) {
            return false;
        }

        const newSteps = newFlow.steps;
        let currentStepKey = !startAtEnd ? 0 : newSteps.length - 1;
        const stepIndexToAdd = !startAtEnd ? 1 : -1;

        let possibleStep = newSteps[currentStepKey];
        if (!possibleStep) {
            return false;
        }

        while (possibleStep.shouldBeSkipped()) {
            // Support going backwards
            if (startAtEnd && currentStepKey + stepIndexToAdd < 0) {
                return false;
            }

            // Support going forwards
            if (!startAtEnd && currentStepKey + stepIndexToAdd >= newSteps.length) {
                return false;
            }

            currentStepKey += stepIndexToAdd;
            possibleStep = newSteps[currentStepKey];
            if (!possibleStep) {
                return false;
            }
        }

        if (!possibleStep.canBeActivated()) {
            return false;
        }

        activeStep.value?.deactivate();

        activeFlowKey.value = newFlowKey;
        activeStepIndex.value = currentStepKey;

        setSteps(newSteps);

        activeStep.value?.activate();

        return true;
    };

    const initializeSteps = () => {
        if (!flows.value) {
            return;
        }

        for (const flow of Object.values(flows.value)) {
            for (const step of flow.steps) {
                step.setup();
            }
        }
    };

    const reset = async () => {
        metaDataStore.reset();
        reservationStore.reset();
        restartWizardStore.reset();
        checkInStore.reset();
        paymentStore.reset();

        await metaDataStore.updateFormConfig(i18n.locale.value.toString());

        activeFlowKey.value = 'default';
        activeStepIndex.value = 0;

        if (activeAccount.value) {
            const flowKey = activeFlowKey.value ?? 'default';
            const flow = activeAccount.value.config.flows[flowKey];
            if (flow) {
                steps.value = flow.steps;
            }
        }

        i18n.locale.value = activeAccount.value?.options.defaultLocale ?? i18n.fallbackLocale.value.toString();

        activeStep.value?.activate();
    };

    watch(activeStepIndex, () => {
        if (activeStepIndex.value > 0) {
            restartWizardStore.stopRestartTimer();
            restartWizardStore.startRestartTimer();
        }
    });

    return {
        // State
        steps,
        flows,
        activeStepIndex,
        activeFlowKey,
        showLoader,

        // Getters
        activeStep,
        activeFlow,
        nextStep,
        defaultFlowIsActive,
        atFirstStepInFlow,
        prevStep,
        totalStepsAmount,
        completedStepsAmount,
        canGoToNextStep,
        showProgressBar,

        // Actions
        setSteps,
        goToNextStep,
        goToPrevStep,
        setFlows,
        selectFlow,
        initializeSteps,
        reset,
    };
});
